import { useEffect, useRef } from "react";

import { isElectronRuntime } from "@/lib/logout";

/**
 * Register the Electron desktop's bundle ID (`com.ano.electron`) with
 * macOS NSUserNotificationCenter on first sign-in, so subsequent
 * `sendNativeNotification` calls actually deliver banners.
 *
 * Why this hook is non-trivial — the Chromium quirk:
 *
 *   `Notification.permission` in Electron's renderer returns `"granted"`
 *   by default (Chromium can't query NSUserNotificationCenter), so
 *   `Notification.requestPermission()` is a no-op and never causes
 *   macOS to create a TCC record. v0.1.61 (PR #1775) tried the
 *   `requestPermission()` path — confirmed not working in prod (no
 *   `tccutil reset Notifications com.ano.electron` record existed on
 *   a real install).
 *
 *   v0.1.62 routes the registration through the main process:
 *   `window.__electronApi.registerNotifications()` invokes
 *   `ano:register-notifications` which fires a real
 *   `electron.Notification` ctor. That goes through to
 *   NSUserNotificationCenter, triggers macOS's first-use permission
 *   dialog, AND creates the TCC record. Subsequent
 *   `sendNativeNotification` calls (also routed through main via
 *   `ano:fire-notification`) then work as expected.
 *
 * Web build: no-op. Browsers handle per-origin permissions natively;
 * showing a permission prompt on page load is a Chrome/Safari
 * anti-pattern and they actively suppress it.
 *
 * Persistence: once we've fired the registration ping successfully,
 * `localStorage["ano:notif:macos-registered:v1"]` remembers it so the
 * user doesn't see the "Notifications enabled" banner on every cold
 * start.
 */

const REGISTERED_KEY = "ano:notif:macos-registered:v1";

interface ElectronNotifRegisterApi {
  registerNotifications?: () => Promise<
    | { ok: true }
    | { ok: false; reason: "not-supported" }
    | { ok: false; reason: "threw"; error: string }
  >;
}

export function useRequestNotificationPermission(enabled: boolean): void {
  const firedRef = useRef(false);
  useEffect(() => {
    if (firedRef.current) return;
    if (!enabled) return;
    if (!isElectronRuntime()) return;
    if (typeof window === "undefined") return;

    // localStorage is the only reliable "have we registered?" signal
    // because Electron's renderer reports `Notification.permission ===
    // "granted"` by default. The persisted flag means we only fire the
    // registration ping once per install.
    try {
      if (window.localStorage.getItem(REGISTERED_KEY) === "1") return;
    } catch {
      // private browsing / quota — fall through; worst case we double-fire
    }

    const api = (
      window as unknown as { __electronApi?: ElectronNotifRegisterApi }
    ).__electronApi;
    if (!api?.registerNotifications) return;

    firedRef.current = true;
    const register = api.registerNotifications;
    void (async () => {
      try {
        const result = await register();
        if (result.ok) {
          try {
            window.localStorage.setItem(REGISTERED_KEY, "1");
          } catch {
            // ignore
          }
        }
        // If !result.ok, leave the flag unset so we retry on the next
        // cold start. The error has already been logged in the main
        // process (see notifications-host.ts).
      } catch {
        // ignore — same retry-on-next-cold-start semantics
      }
    })();
  }, [enabled]);
}
